import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

/**
 * Ordered navigation — one array drives the whole bar.
 *
 * Each entry is either a plain link ({ to, label }) or a menu
 * ({ label, items }). Order here IS the order in the bar, so moving a menu is a
 * one-line change. Contact Us is rendered separately as the closing CTA.
 *
 * Current order: Introduction · Scenarios · Applications · Try It.
 * Try It sits last so the reading path runs introduction → how it is used →
 * where it applies → what you can actually use. It holds two entries on purpose
 * (NanoLM and the visual-memory dashboard it links to) rather than sitting as a
 * one-item menu, and its icons are distinct because both pages carry the same
 * brain mark in their own headers.
 *
 * Products, Design Partners and About Us stay reachable from the footer and from
 * in-page links. Mobile renders the menus as expandable sections
 * (see Navbar.css @media max-width: 1024px).
 */
const NAV_ITEMS = [
  { to: '/', label: 'Introduction' },
  {
    label: 'Scenarios',
    items: [
      { to: '/scenarios', label: 'All Scenarios' },
      { to: '/technology', label: 'How It Works' },
      { to: '/evidence', label: 'Evidence & Benchmarks' },
      { to: '/publications', label: 'Docs & Research' },
      { to: '/trust', label: 'Trust & Security' },
    ],
  },
  { to: '/applications', label: 'Applications' },
  {
    label: 'Try It',
    items: [
      { to: '/nanolm', label: 'NanoLM', icon: '🧠' },
      { to: '/memory', label: 'Visual Memory', icon: '🔮' },
    ],
  },
];

function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close everything whenever the route changes.
  useEffect(() => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenDropdown(null);
  };

  const toggleDropdown = (label) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMobileMenu}>
          <img src="/logo.jpeg" alt="Codentic AI" className="logo-image" />
        </Link>

        <button 
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <ul className={`navbar-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {NAV_ITEMS.map((entry) =>
            entry.items ? (
              <li
                key={entry.label}
                className={`dropdown ${openDropdown === entry.label ? 'active' : ''}`}
              >
                <span
                  className="dropdown-toggle"
                  role="button"
                  tabIndex={0}
                  aria-expanded={openDropdown === entry.label}
                  onClick={() => toggleDropdown(entry.label)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleDropdown(entry.label);
                    }
                  }}
                >
                  {entry.label}
                  <span className="dropdown-arrow">▼</span>
                </span>

                <ul className={`dropdown-menu ${openDropdown === entry.label ? 'active' : ''}`}>
                  {entry.items.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to} onClick={closeMobileMenu}>
                        {item.icon ? `${item.icon} ${item.label}` : item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={entry.to}>
                <Link to={entry.to} onClick={closeMobileMenu}>{entry.label}</Link>
              </li>
            )
          )}

          <li><Link to="/contact" onClick={closeMobileMenu} className="nav-cta">Contact Us</Link></li>
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
